import { useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Address } from '@weave/schema-gen-ts/dist/schemas/phone/address/v1/address_service.pb';
import { useQueryClient } from 'react-query';
import { E911Api, E911QueryKeys, E911Types } from '@frontend/api-e911-addresses';
import { http } from '@frontend/fetch';
import { Trans, useTranslation } from '@frontend/i18n';
import { useMutation } from '@frontend/react-query-helpers';
import { theme } from '@frontend/theme';
import {
  ConfirmationModal,
  EditSimpleIcon,
  ModalControlResponse,
  Table,
  TrashIcon,
  useAlert,
  Text,
  useModalControl,
  TextLink,
  Modal,
  ModalControlModalProps,
  NakedUl,
  ListIcon,
} from '@frontend/design-system';
import { E911AddressModal } from '../../../components/devices/e911/e911-address-modal';
import { EditDeviceAssignmentsToE911 } from '../../../components/devices/e911/e911-edit-device-assignments';
import { DeviceAssignmentsToE911 } from '../../../components/devices/e911/e911-preview-device-assignments';
import { logWarning, logError } from '../../../utils/loggers';

type Props = {
  addressModalControl: ModalControlResponse;
  locationId: string;
  addresses: E911Types.ListAddressesIO['output']['addresses'];
  tenantId: string;
};
export const E911AddressesTable = ({ addressModalControl, locationId, addresses, tenantId }: Props) => {
  const { t } = useTranslation('phone', { keyPrefix: 'e911' });
  const alerts = useAlert();
  const queryClient = useQueryClient();
  const addressToEdit = useRef<Address>();
  const confirmDeleteModal = useModalControl();
  const termConditionsModal = useModalControl();
  const deviceAssignmentsModal = useModalControl();
  const editDeviceAssignmentsModal = useModalControl();

  const [deviceIds, setDeviceIds] = useState<string[]>([]);
  const [e911Name, setE911Name] = useState<string>('');
  const [e911Id, setE911Id] = useState<string>('');

  const handleAddressMutateError = (error: unknown, isUpdate?: boolean) => {
    if (http.isHttpError(error) && error.status === 422) {
      alerts.error(t('Unable to verify address with your E911 provider'));
      logWarning('E911 address failed validation with E911 provider', {
        name: isUpdate ? 'E911AddressesTable:updateAddress' : 'E911AddressesTable:createAddress',
        data: {
          tenantId,
          e911Id,
          errorStatus: error.status,
          errorData: error.data,
        },
      });
    } else {
      if (isUpdate) {
        alerts.error(t('Failed to update E911 address'));
        logError('E911AddressesTable:updateAddress | Failed to update E911 address', {
          data: { tenantId, e911Id, error },
        });
      } else {
        alerts.error(t('Failed to add E911 address'));
        logError('E911AddressesTable:createAddress | Failed to add E911 address', {
          data: { tenantId, e911Id, error },
        });
      }
    }
  };

  const resetAddress = () => {
    addressModalControl.closeModal();
    addressToEdit.current = undefined;
  };

  const { mutate: updateAddress, isLoading: isUpdatingAddress } = useMutation(
    (address: Address) => E911Api.update({ ...address, tenantId: tenantId ?? '' }),
    {
      onSuccess: () => {
        alerts.success(t('Address updated successfully'));
        queryClient.invalidateQueries([tenantId, ...E911QueryKeys.queryKeys.listAddresses()]);
        resetAddress();
      },
      onError: (errorCode: number) => handleAddressMutateError(errorCode, true),
    }
  );

  const { mutate: createAddress, isLoading: isLoadingCreateAddress } = useMutation(
    (address: Address) => E911Api.create({ ...address, tenantId: tenantId ?? '' }),
    {
      onSuccess: () => {
        alerts.success(t('Address created successfully'));
        queryClient.invalidateQueries([tenantId, ...E911QueryKeys.queryKeys.listAddresses()]);
        addressModalControl.closeModal();
      },
      onError: (errorCode) => handleAddressMutateError(errorCode),
    }
  );

  const { mutate: deleteAddress } = useMutation(
    ({ addressId }: { addressId: string }) => E911Api.deleteAddress({ addressId, locationId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([tenantId, ...E911QueryKeys.queryKeys.listAddresses()]);
        alerts.success(t('Address deleted successfully'));
        addressToEdit.current = undefined;
      },
      onError: (error) => {
        alerts.error(t('Address failed to delete'));
        logError('E911AddressesTable:deleteAddress | Failed to delete E911 address', {
          data: { tenantId, e911Id, error },
        });
      },
    }
  );

  const initalDeviceIds = deviceIds?.reduce((acc, deviceId) => {
    return {
      ...acc,
      [deviceId]: true,
    };
  }, {});

  return (
    <>
      <Table
        emptyStateConfig={{
          type: 'sync_your_phone',
          header: t('No Addresses'),
          description: t('Add an E911 address to assign to your devices for compliance with Weave phones.'),
          action: {
            label: t('Add Address'),
            onClick: () => addressModalControl.openModal(),
          },
        }}
        fullHeight
        hasGlobalSearch
        data={addresses ?? []}
        colConfig={[
          {
            Header: t('Name'),
            accessor: ({ name }) => name,
            id: 'name',
          },
          {
            Header: t('Address'),
            accessor: ({ city, country, postalCode, state, street, streetSecondary }) =>
              `${street}, ${streetSecondary ? `${streetSecondary}, ` : ''}${city}, ${state} ${postalCode}, ${country}`,
            disableSortBy: true,
            id: 'address',
          },
          {
            Header: t('Assigned Devices'),
            accessor: ({ name, usage, id }) => ({ name, usage, id }),
            cellRenderer: ({ id, usage, name }) => {
              const count = usage.deviceIds.length;
              return (
                <TextLink
                  onClick={() => {
                    setDeviceIds(usage.deviceIds);
                    setE911Name(name);
                    setE911Id(id);
                    if (count > 0) {
                      deviceAssignmentsModal.openModal();
                    } else editDeviceAssignmentsModal.openModal();
                  }}
                >
                  {t('{{count}} Devices', { count })}
                </TextLink>
              );
            },
            id: 'devices-assigned-to-address',
            disableSortBy: true,
          },
        ]}
        rowActions={{
          actions: [
            {
              Icon: EditSimpleIcon,
              label: t('Edit Address'),
              onClick: (address) => {
                addressToEdit.current = address;
                addressModalControl.openModal();
              },
            },
            {
              Icon: ListIcon,
              label: t('Edit Device Assignments'),
              onClick: ({ id, name, usage }) => {
                setDeviceIds(usage.deviceIds);
                setE911Name(name);
                setE911Id(id);
                editDeviceAssignmentsModal.openModal();
              },
            },
            {
              Icon: TrashIcon,
              destructive: true,
              label: t('Delete Address'),
              onClick: (address) => {
                addressToEdit.current = address;
                confirmDeleteModal.openModal();
              },
            },
          ],
        }}
      />
      <E911AddressModal
        isLoading={isLoadingCreateAddress || isUpdatingAddress}
        {...addressModalControl.modalProps}
        onSave={addressToEdit.current ? updateAddress : createAddress}
        address={addressToEdit.current}
        onCancel={resetAddress}
      />
      <DeviceAssignmentsToE911
        e911Name={e911Name}
        deviceIds={deviceIds}
        openEdit={editDeviceAssignmentsModal.openModal}
        {...deviceAssignmentsModal.modalProps}
      />
      <EditDeviceAssignmentsToE911
        e911Id={e911Id}
        initalDeviceIds={initalDeviceIds}
        e911Name={e911Name}
        {...editDeviceAssignmentsModal.modalProps}
      />
      <ConfirmationModal
        maxWidth={600}
        title={t('Delete Address')}
        message={
          <Text as='div' css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            <Text>Deleting this address will also remove its assignment from your devices.</Text>
            <Text>
              {addresses?.length === 1 ? (
                <Trans t={t}>
                  You need to have at least one E911 address that is assigned to all of your devices to be in
                  <TextLink onClick={termConditionsModal.openModal}>compliance with E911.</TextLink>
                </Trans>
              ) : (
                <Trans t={t}>
                  You may need to update the E911 address of the affected devices so that emergency services can
                  accurately respond to the user’s physical location.
                </Trans>
              )}
            </Text>
            <Text>
              <Trans t={t} name={addressToEdit.current?.name}>
                Are you sure you want to delete <strong>{{ name: addressToEdit.current?.name }}</strong>?
              </Trans>
            </Text>
          </Text>
        }
        {...confirmDeleteModal.modalProps}
        onCancel={() => {
          addressToEdit.current = undefined;
        }}
        onClose={() => {
          confirmDeleteModal.closeModal();
          addressToEdit.current = undefined;
        }}
        confirmLabel={t('Delete Address')}
        onConfirm={() => deleteAddress({ addressId: addressToEdit.current?.id ?? '' })}
        destructive
      />
      <E911ComplicaneModal {...termConditionsModal.modalProps} />
    </>
  );
};

const E911ComplicaneModal = (modalProps: ModalControlModalProps) => {
  const { t } = useTranslation('phone', { keyPrefix: 'e911' });

  return (
    <Modal {...modalProps} maxWidth={800}>
      <Modal.Header onClose={modalProps.onClose}>{t('E911 Setup Terms and Conditions')}</Modal.Header>
      <Modal.Body
        css={css`
          margin: ${theme.spacing(2, 0)};
        `}
      >
        <Text>
          {t(
            'We want to make sure that you are aware of important differences in the way 9-1-1 service operates with a VoIP phone when compared with traditional telephone service. Here is what you need to keep in mind:'
          )}
        </Text>
        <NakedUl
          css={css`
            display: flex;
            list-style: disc;
            flex-direction: column;
            gap: ${theme.spacing(2)};
            margin: ${theme.spacing(2, 0)};
            padding: ${theme.spacing(0, 2)};
          `}
        >
          <li>
            {t(
              'With VoIP phone service, your 9-1-1 call is forwarded to a third-party service provider that will automatically or manually route your call to the emergency response center.'
            )}
          </li>
          <li>
            {t(
              'Because you can move your VoIP phone between locations and because, you must immediately inform the emergency operator of your location and contact particulars any time you call 9-1-1.'
            )}
          </li>
          <li>
            {t(
              'Weave will attempt to provide the emergency operator with your service address, so please ensure that your information on file with us is always accurate and updated. If you are unable to provide your location to the emergency operator, emergency services will be dispatched to the address you have provided and may be misdirected to the wrong location.'
            )}
          </li>
          <li>
            {t(
              'VoIP phone service depends not only on your continued subscription for the service, but also on Internet connectivity and power to function. In the event of power, network, or Internet outages, or if your service is disconnected or suspended, you may experience a failure, disruption, or delay in your 9-1-1 service. Do not disconnect your 9-1-1 call until you are told to do so by the emergency operator.'
            )}
          </li>
          <li>
            {t(
              'You must notify members of staff and other potential users of your VoIP phone service of the nature and limitations of 9-1-1 emergency calls. Weave has provided stickers that can be placed on your phones for this purpose.'
            )}
          </li>
          <li>
            {t(
              'If you are not comfortable with the limitations of 9-1-1 emergency calls, we recommend that you terminate the VoIP services or consider an alternate means for accessing traditional 9-1-1 emergency services, such as a cellular telephone.'
            )}
          </li>
        </NakedUl>
        <Text>
          <Trans t={t}>
            To review the 9-1-1 limitations, including 9-1-1 disclaimers and limitations of liability, and learn more
            about E911 service, visit{' '}
            <TextLink rel='noreferrer' target='_blank' href='https://www.getweave.com/legal/e911/'>
              https://www.getweave.com/legal/e911/.
            </TextLink>
          </Trans>
        </Text>
      </Modal.Body>
    </Modal>
  );
};
